/**
 * Shared phone / email format validators — the single source the form
 * components converge on (previously each form carried its own copy).
 *
 * Both validate a NON-empty value; optionality composes at the call site
 * (`v === '' || isValidPhone(v)`), since one form's optional field is another's
 * required one.
 */
import { normalizeE164 } from '../../shared/phone'

/**
 * True when the value is a complete, sendable phone number.
 *
 * US-centric to match `formatPhone`'s mask: a non-"+" value must carry exactly
 * 10 digits (or 11 with the leading 1 country code) — a partial "(415) 594-07"
 * is invalid rather than falling into E.164's lenient 8–15-digit international
 * branch. An explicit "+" number defers to `normalizeE164` (shared/phone.ts),
 * the same normalizer the SMS send path runs, so "valid here" always means
 * "the backend can text it".
 */
export function isValidPhone(v: string): boolean {
  const trimmed = v.trim()
  if (!trimmed) return false
  if (trimmed.startsWith('+')) return normalizeE164(trimmed) !== null
  const digits = trimmed.replace(/\D/g, '')
  const usShaped = digits.length === 10
    || (digits.length === 11 && digits.startsWith('1'))
  return usShaped && normalizeE164(trimmed) !== null
}

/** True when the value looks like a deliverable email address. */
export function isValidEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())
}
